import { useState, useEffect } from "react";
import { Grid, Paper, IconButton, Box, CircularProgress } from "@mui/material";
import axios from "axios";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";

import { Client } from "../../models/client/client.model";
import VisitClientSelectionModal from "../../components/modals/visits/VisitClientSelectionModal";
import VisitCheckinCard from "../../components/cards/visits/VisitCheckinCard";
import VisitCheckoutCard from "../../components/cards/visits/VisitCheckoutCard";
import VisitTopTabNavigation from "../../components/navigation/visits/VisitTopTabNavigation";
import Colors from "../../constants/Colors";
import usePrompt from "../../hooks/customNavigationHooks";

export const VisitsManagementPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { visitData?: any };

  // Local state for the Visit
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [checkinDate, setCheckinDate] = useState<string>("");
  const [checkinTime, setCheckinTime] = useState<string>("");

  // Modals and UI State
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isStartingVisit, setIsStartingVisit] = useState(false);
  const [isVisitActive, setIsVisitActive] = useState(false);
  const [topTabValue, setTopTabValue] = useState<number>(0);
  const [startCoordinates, setStartCoordinates] =
    useState<GeolocationCoordinates | null>(null);

  // Checkout State
  const [checkoutDateFinal, setCheckoutDateFinal] = useState<string>("");
  const [checkoutTimeFinal, setCheckoutTimeFinal] = useState<string>("");
  const [visitPayload, setVisitPayload] = useState<any>({});
  const [isVisitFinished, setIsVisitFinished] = useState(false);
  const [isCheckingActive, setIsCheckingActive] = useState(false);

  useEffect(() => {
    const visitData = state?.visitData;

    if (visitData) {
      // Initialize from navigation state
      setCheckinDate(visitData.dataCheckin || "");
      setCheckinTime(visitData.horaCheckin || "");
      setCheckoutDateFinal(visitData.dataCheckout || "");
      setCheckoutTimeFinal(visitData.horaCheckout || "");
      setVisitPayload(visitData);

      if (visitData.clienteId) {
        setSelectedClient({
          id: visitData.clienteId,
          apelido: visitData.clienteApelido || "",
          nome: visitData.clienteNome || "",
        } as Client);
      }

      if (visitData.latitudeCheckin && visitData.longitudeCheckin) {
        setStartCoordinates({
          latitude: Number(visitData.latitudeCheckin),
          longitude: Number(visitData.longitudeCheckin),
          accuracy: Number(visitData.accuracyCheckin || 0),
        } as GeolocationCoordinates);
      }

      if (visitData.situacao === "2") {
        setIsVisitFinished(true);
        setIsVisitActive(false);
        setTopTabValue(0); // Start on checkin tab as requested
      } else if (visitData.situacao === "1") {
        setIsVisitFinished(false);
        setIsVisitActive(true);
        setTopTabValue(0);
      }
    } else {
      // Default initialization for new visit
      const checkActiveVisit = async () => {
        setIsCheckingActive(true);
        try {
          const API_URL = localStorage.getItem("apiUrl");
          const token = localStorage.getItem("token");
          const userId = localStorage.getItem("userId");

          const rolesStr = localStorage.getItem("roles");
          const roles = rolesStr ? JSON.parse(rolesStr) : [];

          let query = `situacao=1`;
          if (roles.length > 0) {
            query += `&vendedorId=${roles.join(",")}`;
          } else {
            if (userId) query += `&vendedorId=${userId}`;
          }

          const response = await axios.get(
            `${API_URL}/visitas?${query}&page=1`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          if (
            response.data &&
            response.data.visitas &&
            response.data.visitas.length > 0
          ) {
            // Filtra apenas a visita que pertence ao próprio usuário logado
            const myVisit = response.data.visitas.find(
              (v: any) => v.vendedorId === userId,
            );

            if (myVisit) {
              alert(
                "Você já possui um check-in em aberto. Redirecionando para a visita atual...",
              );
              navigate("/visitas/detalhes", {
                state: {
                  id: myVisit.id,
                  visitData: myVisit,
                },
                replace: true,
              });
              return;
            }
          }
        } catch (error) {
          console.error("Erro ao verificar visita ativa", error);
        } finally {
          setIsCheckingActive(false);
        }

        const now = new Date();
        const day = String(now.getDate()).padStart(2, "0");
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const year = now.getFullYear();
        setCheckinDate(`${day}/${month}/${year}`);

        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const seconds = String(now.getSeconds()).padStart(2, "0");
        setCheckinTime(`${hours}:${minutes}:${seconds}`);
      };

      checkActiveVisit();
    }
  }, [location.state, navigate]);

  const isDirty = (!!selectedClient || isVisitActive) && !isVisitFinished;

  usePrompt(
    "Deseja sair da página? As mudanças feitas serão perdidas.",
    isDirty,
  );

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const handleStartVisit = async (coords: GeolocationCoordinates) => {
    setIsStartingVisit(true);
    setStartCoordinates(coords);

    const API_URL = localStorage.getItem("apiUrl");
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId") || "";
    const normalizedClientNickname = selectedClient?.apelido?.trim();
    const normalizedClientName = selectedClient?.nome?.trim() || "";
    const hasValidNickname =
      !!normalizedClientNickname &&
      normalizedClientNickname.toUpperCase() !== "N/A";

    const partialPayload = {
      id: `${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, "0")}${String(new Date().getDate()).padStart(2, "0")}.${String(new Date().getHours()).padStart(2, "0")}${String(new Date().getMinutes()).padStart(2, "0")}${String(new Date().getSeconds()).padStart(2, "0")}.${userId}`,
      owner: userId,
      doSync: "0",
      syncAt: new Date().getTime(),
      situacao: "1",
      clienteId: selectedClient?.id,
      clienteApelido: hasValidNickname ? normalizedClientNickname : "",
      clienteNome: normalizedClientName,
      vendedorId: userId,
      dataCheckin: checkinDate,
      horaCheckin: checkinTime,
      accuracyCheckin: String(coords.accuracy),
      latitudeCheckin: String(coords.latitude),
      longitudeCheckin: String(coords.longitude),
    };

    try {
      await axios.post(`${API_URL}/visitas`, partialPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setVisitPayload(partialPayload);

      setIsVisitActive(true);
      setTopTabValue(1);
    } catch (error) {
      console.error("Erro ao realizar check-in:", error);
      alert("Erro ao realizar check-in no servidor. Verifique sua conexão.");
    } finally {
      setIsStartingVisit(false);
    }
  };

  const handleCheckoutClick = async (
    cDate: string,
    cTime: string,
    description: string,
    types: string[],
    otherDesc: string,
    coords: GeolocationCoordinates,
  ) => {
    setCheckoutDateFinal(cDate);
    setCheckoutTimeFinal(cTime);

    const API_URL = localStorage.getItem("apiUrl");
    const token = localStorage.getItem("token");

    try {
      if (!API_URL || !token) {
        throw new Error("API_URL ou token ausente");
      }
      if (!visitPayload?.id) {
        throw new Error("visitPayload.id ausente (não existe check-in ativo)");
      }

      const finalPayload = {
        ...visitPayload,
        situacao: "2",
        descricao: description,
        observacao: otherDesc,
        tipoVisita: types,
        dataCheckout: cDate,
        horaCheckout: cTime,
        accuracyCheckout: String(coords.accuracy),
        latitudeCheckout: String(coords.latitude),
        longitudeCheckout: String(coords.longitude),
        owner: visitPayload.owner || localStorage.getItem("userId") || "",
        vendedorId:
          visitPayload.vendedorId || localStorage.getItem("userId") || "",
      };

      await axios.post(`${API_URL}/visitas`, finalPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setVisitPayload(finalPayload);
      setIsVisitFinished(true);
      setIsVisitActive(false);

      alert("Visita finalizada com sucesso!");
      navigate("/visitas", { replace: true });
    } catch (error) {
      console.error("Erro ao realizar checkout:", error);
      alert("Erro ao finalizar visita no servidor. Verifique sua conexão.");
    }
  };

  if (isCheckingActive) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* Main Content */}
      <VisitClientSelectionModal
        openModal={isClientModalOpen && !isVisitActive && !isVisitFinished}
        toggle={() => setIsClientModalOpen(false)}
        onSelectClient={(client) => setSelectedClient(client)}
      />

      <VisitTopTabNavigation
        currentTab={topTabValue}
        changeTab={setTopTabValue}
        isCheckoutEnabled={isVisitActive || isVisitFinished}
      />

      <Grid
        container
        justifyContent={"center"}
        alignItems={"flex-start"} // top aligned
        width={"100%"}
        sx={{ pb: 10 }} // Padding bottom for the absolute paper from layout if any
      >
        <Grid
          container
          spacing={1}
          justifyContent={"center"}
          width={{
            md: "40%",
            xs: "95%",
          }}
        >
          <Box
            sx={{
              display: topTabValue === 0 ? "block" : "none",
              width: "100%",
            }}
          >
            <VisitCheckinCard
              selectedClient={selectedClient}
              toggleModal={() => setIsClientModalOpen(true)}
              checkinDate={checkinDate}
              checkinTime={checkinTime}
              isStarting={isStartingVisit}
              onStartVisit={handleStartVisit}
              startCoordinates={startCoordinates}
              isLocked={isVisitActive || isVisitFinished}
              isVisible={topTabValue === 0}
            />
          </Box>

          <Box
            sx={{
              display: topTabValue === 1 ? "block" : "none",
              width: "100%",
            }}
          >
            <VisitCheckoutCard
              isActive={isVisitActive || isVisitFinished}
              onCheckout={handleCheckoutClick}
              checkoutDate={isVisitFinished ? checkoutDateFinal : ""}
              checkoutTime={isVisitFinished ? checkoutTimeFinal : ""}
              checkinDate={checkinDate}
              checkinTime={checkinTime}
              initialDescription={visitPayload?.descricao}
              initialTypes={visitPayload?.tipoVisita}
              initialOtherDesc={visitPayload?.observacao}
            />
          </Box>
        </Grid>
      </Grid>
    </>
  );
};
